import { categories } from "@/mocks/categories";

export type SortKey = "year" | "rating" | "popularity";

interface FilterBarProps {
  selectedGenres: string[];
  toggleGenre: (key: string) => void;
  yearFrom: string;
  yearTo: string;
  setYearFrom: (v: string) => void;
  setYearTo: (v: string) => void;
  minRating: string;
  setMinRating: (v: string) => void;
  sort: SortKey;
  setSort: (v: SortKey) => void;
  onReset: () => void;
  mobileOpen: boolean;
  setMobileOpen: (v: boolean) => void;
}

const YEAR_OPTIONS = ["2024", "2023", "2022"];
const RATING_OPTIONS = [
  { value: "", label: "不限评分" },
  { value: "8.5", label: "8.5 分以上" },
  { value: "8.0", label: "8.0 分以上" },
  { value: "7.0", label: "7.0 分以上" },
];

export default function FilterBar(props: FilterBarProps) {
  const {
    selectedGenres,
    toggleGenre,
    yearFrom,
    yearTo,
    setYearFrom,
    setYearTo,
    minRating,
    setMinRating,
    sort,
    setSort,
    onReset,
    mobileOpen,
    setMobileOpen,
  } = props;

  const selectClass =
    "px-3 py-2 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-700 outline-none focus:border-primary-400 cursor-pointer";

  return (
    <div className="w-full rounded-lg bg-background-100 border border-background-200">
      {/* mobile toggle */}
      <button
        type="button"
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden w-full flex items-center justify-between px-4 py-3 cursor-pointer"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
          <i className="ri-filter-3-line" /> 筛选与排序
        </span>
        <i className={mobileOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
      </button>

      <div
        className={`${mobileOpen ? "block" : "hidden"} lg:block px-4 md:px-5 py-4 border-t lg:border-t-0 border-background-200`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* genres */}
          <div className="lg:col-span-6">
            <div className="text-xs font-semibold text-foreground-500 mb-2 uppercase tracking-wider">
              类型（可多选）
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => {
                const active = selectedGenres.includes(cat.key);
                return (
                  <button
                    key={cat.key}
                    type="button"
                    onClick={() => toggleGenre(cat.key)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      active
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-50 border border-background-300 text-foreground-600 hover:border-primary-300"
                    }`}
                  >
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* year range */}
          <div className="lg:col-span-3">
            <div className="text-xs font-semibold text-foreground-500 mb-2 uppercase tracking-wider">
              上映年份区间
            </div>
            <div className="flex items-center gap-2">
              <select
                value={yearFrom}
                onChange={(e) => setYearFrom(e.target.value)}
                className={selectClass}
              >
                <option value="">起始年份</option>
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>
                    {y} 年
                  </option>
                ))}
              </select>
              <span className="text-foreground-400">—</span>
              <select
                value={yearTo}
                onChange={(e) => setYearTo(e.target.value)}
                className={selectClass}
              >
                <option value="">结束年份</option>
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>
                    {y} 年
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* rating + sort */}
          <div className="lg:col-span-3">
            <div className="text-xs font-semibold text-foreground-500 mb-2 uppercase tracking-wider">
              评分与排序
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={minRating}
                onChange={(e) => setMinRating(e.target.value)}
                className={selectClass}
              >
                {RATING_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                className={selectClass}
              >
                <option value="year">按上映时间</option>
                <option value="rating">按评分</option>
                <option value="popularity">按热门程度</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-600 hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" /> 重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}